import { useTranslation } from "react-i18next";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Tile } from "@carbon/react";
import { TimePoint } from "../../../types";
import { formatCurrency } from "../utils/kpiHelpers";
import { toMonthlySeries } from "./chartData";
import "./analyticsCharts.scss";

interface SpendOverTimeChartProps {
  timeSeries: TimePoint[];
  /** Active currency: only this currency's points are plotted (never summed across currencies). */
  currency: string;
}

// Palette is `--cds-link-primary`/`--cds-support-*` with literal fallbacks — no `--cds-chart-*`
// tokens ship with this Carbon version, and an undefined `var()` voids the whole declaration.
const SPEND_COLOR = "var(--cds-link-primary, #0f62fe)";
const AXIS_COLOR = "var(--cds-text-secondary, #525252)";

export const SpendTooltip = ({
  active,
  payload,
  label,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
  currency: string;
}) => {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  return (
    <div className="analytics-chart__tooltip">
      <p className="analytics-chart__tooltip-title">{label}</p>
      <p className="analytics-chart__tooltip-row">
        {`${t("analytics.charts.spend")}: ${formatCurrency(payload[0].value, currency)}`}
      </p>
    </div>
  );
};

const SpendOverTimeChart = ({
  timeSeries,
  currency,
}: SpendOverTimeChartProps) => {
  const { t, i18n } = useTranslation();
  const data = toMonthlySeries(timeSeries, currency, i18n.language);

  if (data.length === 0) {
    return (
      <Tile className="analytics-chart">
        <h3 className="analytics-chart__title">
          {t("analytics.charts.spendOverTime")}
        </h3>
        <p className="analytics-chart__empty">{t("analytics.charts.noData")}</p>
      </Tile>
    );
  }

  return (
    <Tile className="analytics-chart">
      <h3 className="analytics-chart__title">
        {t("analytics.charts.spendOverTime")}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--cds-border-subtle, #e0e0e0)"
          />
          <XAxis
            dataKey="label"
            fontSize={12}
            stroke={AXIS_COLOR}
            tick={{ fill: AXIS_COLOR }}
          />
          <YAxis
            tickFormatter={(value) => formatCurrency(value, currency)}
            stroke={AXIS_COLOR}
            tick={{ fill: AXIS_COLOR }}
          />
          <Tooltip content={<SpendTooltip currency={currency} />} />
          {/* No entry animation: dots (and so every month) are present on first paint, deterministic in tests. `dot` keeps a lone month visible: an area/line with a single point draws no path. */}
          <Area
            type="monotone"
            dataKey="spend"
            stroke={SPEND_COLOR}
            fill={SPEND_COLOR}
            fillOpacity={0.2}
            strokeWidth={2}
            dot={{ r: 4, fill: SPEND_COLOR, stroke: SPEND_COLOR }}
            activeDot={{ r: 6 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Tile>
  );
};

export default SpendOverTimeChart;
